import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useSpring } from "framer-motion";
import { api, get, imgUrl, BACKEND_URL } from "@/lib/api";
import { useSeo, useTexts } from "@/lib/layout";
import { fmtDate, fmtTime } from "@/lib/format";
import { Seo, orgLd, breadcrumbLd, absUrl } from "@/components/site/Seo";
import { Cover, GridStory, SectionHeader } from "@/components/site/Stories";
import { MostRead } from "@/components/site/MostRead";
import { ShareBar } from "@/components/site/ShareBar";
import { Claves, Faqs, Tags } from "@/components/site/ArticleBlocks";
import NotFound from "@/pages/NotFound";

const Progress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });
  return <motion.div style={{ scaleX }} className="fixed top-0 inset-x-0 h-[3px] bg-primary origin-left z-[60]" data-testid="reading-progress" />;
};

const buildLd = (a, url, image, seo, site, home) => {
  const crumbs = [{ name: home, path: "/" }, { name: a.category_name, path: `/seccion/${a.category_slug}` }, { name: a.title, path: `/noticia/${a.slug}` }];
  const article = {
    "@context": "https://schema.org",
    "@type": a.schema_type || seo.default_schema_type || "NewsArticle",
    headline: a.title,
    description: a.summary,
    image: image ? [image] : undefined,
    datePublished: a.published_at,
    dateModified: a.updated_at || a.published_at,
    author: [{ "@type": "Organization", name: a.author || site, url: absUrl(seo.publishing_principles_url || "/sobre-nosotros") }],
    publisher: orgLd(seo, site),
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    articleSection: a.category_name,
    keywords: (a.tags || []).join(", "),
    inLanguage: "es-ES",
  };
  if (seo.speakable !== false) article.speakable = { "@type": "SpeakableSpecification", cssSelector: ["h1", ".claves"] };
  const ld = [article, breadcrumbLd(crumbs)];
  if (a.faqs?.length && seo.show_faqs !== false)
    ld.push({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: a.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) });
  return ld;
};

const ArticleSkeleton = () => (
  <div className="wrap pt-10 max-w-4xl animate-pulse space-y-5" data-testid="article-loading">
    <div className="h-4 w-32 bg-muted" /><div className="h-14 bg-muted" /><div className="h-6 w-3/4 bg-muted" /><div className="aspect-[16/9] bg-muted" />
  </div>
);

export default function ArticlePage() {
  const { slug } = useParams();
  const { data, isLoading, isError } = useQuery({ queryKey: ["article", slug], queryFn: () => get(`/public/articles/${slug}`), retry: false });
  const { data: mostRead } = useQuery({ queryKey: ["most-read"], queryFn: () => get("/public/most-read") });
  const { seo, site } = useSeo();
  const t = useTexts("article");
  useEffect(() => { api.post(`/public/articles/${slug}/view`).catch(() => {}); }, [slug]);

  if (isLoading) return <ArticleSkeleton />;
  if (data?.redirect) return <Navigate to={`/noticia/${data.redirect}`} replace />;
  if (isError || !data) return <NotFound />;
  const { article: a, related } = data;
  const url = a.canonical_url || `${window.location.origin}/noticia/${a.slug}`;
  const shareUrl = `${BACKEND_URL}/api/share/noticia/${a.slug}`;
  const image = imgUrl(a.image_url) || undefined;

  return (
    <>
      <Progress />
      <Seo
        title={a.seo_title || a.title}
        description={a.seo_description || a.summary}
        image={image}
        type="article"
        canonical={a.canonical_url || undefined}
        noindex={a.noindex}
        social={{ title: a.social_title || undefined, description: a.social_description || undefined, image: imgUrl(a.social_image_url) || undefined }}
        article={{ published_time: a.published_at, modified_time: a.updated_at, section: a.category_name }}
        jsonLd={buildLd(a, url, image, seo, site, t.breadcrumb_home)}
      />
      <article className="wrap pt-8 md:pt-12" data-testid="article-page">
        <nav aria-label="Ruta" className="kicker text-[10px] text-muted-foreground flex flex-wrap gap-2 mb-6" data-testid="article-breadcrumbs">
          <Link to="/" className="hover:text-primary">{t.breadcrumb_home}</Link><span>/</span>
          <Link to={`/seccion/${a.category_slug}`} className="text-primary hover:underline">{a.category_name}</Link>
        </nav>
        <header className="max-w-4xl">
          <h1 className="font-display font-extrabold tracking-[-0.02em] leading-[1.04] text-4xl sm:text-5xl lg:text-6xl" data-testid="article-title">{a.title}</h1>
          <p className="mt-6 font-serif text-xl sm:text-2xl leading-relaxed text-muted-foreground" data-testid="article-summary">{a.summary}</p>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-5 border-y border-border py-4">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 grid place-items-center bg-primary text-primary-foreground font-display font-black">N</span>
              <div className="text-sm leading-tight">
                <p className="font-semibold" data-testid="article-author">{a.author}</p>
                <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground mt-1">
                  <time dateTime={a.published_at}>{fmtDate(a.published_at)} · {fmtTime(a.published_at)}</time> · {a.reading_time} {t.reading_label}
                </p>
              </div>
            </div>
            <ShareBar title={a.title} url={url} shareUrl={shareUrl} />
          </div>
        </header>
        <figure className="mt-8">
          <Cover a={a} eager className="aspect-[16/9] max-h-[640px] w-full" />
          {a.image_credit && <figcaption className="mt-2 font-mono text-[11px] text-muted-foreground">{a.image_credit}</figcaption>}
        </figure>
        <div className="grid grid-cols-12 gap-y-14 lg:gap-x-14 mt-12">
          <div className="col-span-12 lg:col-span-8 max-w-[720px]">
            {seo.show_key_points !== false && <Claves points={a.key_points} title={seo.key_points_title} />}
            <div className="article-body dropcap" data-testid="article-body" dangerouslySetInnerHTML={{ __html: a.body }} />
            {seo.show_faqs !== false && <Faqs faqs={a.faqs} title={seo.faq_title} />}
            <Tags tags={a.tags} />
            <div className="mt-10 pt-6 border-t border-border"><ShareBar title={a.title} url={url} shareUrl={shareUrl} id="bottom" /></div>
          </div>
          <aside className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-20"><MostRead items={mostRead || []} /></div>
          </aside>
        </div>
      </article>
      {related?.length > 0 && (
        <section className="wrap mt-20" data-testid="related-articles">
          <SectionHeader title={t.related_title} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {related.map((r, i) => <GridStory key={r.id} a={r} testid={`related-story-${i}`} />)}
          </div>
        </section>
      )}
    </>
  );
}
